/* ==========================================================================
   Brake Rotor Thickness Calculator
   Self-contained. Loaded only on this calculator's page.
   No shared dependencies.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-pc-rtc]');
  if (!root) return;

  var MM_PER_INCH = 25.4;
  /* A cleanup cut takes material from both faces. 0.5mm total is a light cut,
     and most lathes cannot reliably do less. */
  var CLEANUP_CUT_MM = 0.5;
  /* Machine-to spec: leave margin above discard so the rotor still has service
     life after the cut rather than arriving at the limit immediately. */
  var MACHINE_MARGIN_MM = 0.5;

  /* ====================================================================
     compute(input) -> result

     PURE. No DOM, no network, no globals. All calculator logic lives here
     so it can be reasoned about and tested in isolation.
     ==================================================================== */
  function compute(input) {
    var measured = input.unit === 'in' ? input.measured * MM_PER_INCH : input.measured;
    if (!isFinite(measured) || measured <= 0) {
      return { ok: false, errors: ['Enter the measured rotor thickness.'] };
    }
    if (!isFinite(input.discard) || input.discard <= 0) {
      return { ok: false, errors: ['Enter the discard spec stamped on the rotor hat.'] };
    }
    if (measured < input.discard - 10) {
      return { ok: false, errors: ['That measurement is far below the discard spec. Check you are using the same units for both.'] };
    }

    var above = measured - input.discard;
    var afterCut = measured - CLEANUP_CUT_MM;
    var canMachine = afterCut >= input.discard + MACHINE_MARGIN_MM;

    var status, warnings = [];
    if (measured <= input.discard) {
      status = { key: 'critical', label: 'Replace, below discard',
                 note: 'At or under the minimum thickness. The rotor cannot dissipate heat safely and must not be machined.' };
    } else if (input.scored) {
      status = canMachine
        ? { key: 'soon', label: 'Machine or replace',
            note: 'There is enough material to clean up, but scoring this bad often means a deeper cut than the standard 0.5mm.' }
        : { key: 'now', label: 'Replace',
            note: 'Damaged and too thin to machine out. Replace it.' };
    } else if (!canMachine) {
      status = { key: 'now', label: 'Replace, too thin to machine',
                 note: 'Only ' + num(above, 2) + 'mm above discard. A cleanup cut would put it at or under the limit.' };
    } else if (above < 1.5) {
      status = { key: 'soon', label: 'Serviceable, nearly done',
                 note: num(above, 2) + 'mm above discard. It will not survive another pad change.' };
    } else {
      status = { key: 'good', label: 'Serviceable',
                 note: num(above, 2) + 'mm of material above the discard spec.' };
    }

    if (measured > input.discard && above > 0 && above < CLEANUP_CUT_MM) {
      warnings.push('Less than a single cleanup cut of material remains above the limit.');
    }

    var figures = [
      { label: 'Above discard spec', value: num(above, 2) + ' mm',
        sub: num(measured, 2) + 'mm measured against a ' + num(input.discard, 2) + 'mm limit.' },
      { label: 'Can it be machined', value: canMachine ? 'Yes' : 'No',
        sub: canMachine
          ? 'A 0.5mm cleanup cut leaves ' + num(afterCut - input.discard, 2) + 'mm above the limit.'
          : 'A cleanup cut would leave it at or below the discard spec.' }
    ];

    var rows = [
      { head: 'Measurements' },
      { label: 'Measured', value: num(measured, 2) + ' mm  (' + num(measured / MM_PER_INCH, 3) + ' in)' },
      { label: 'Discard spec', value: num(input.discard, 2) + ' mm' },
      { label: 'Above limit', value: num(above, 2) + ' mm' },
      { label: 'After a 0.5mm cut', value: num(afterCut, 2) + ' mm' }
    ];

    if (isFinite(input.newThick) && input.newThick > input.discard) {
      var total = input.newThick - input.discard;
      var used = ((input.newThick - measured) / total) * 100;
      rows.push({ head: 'Wear' });
      rows.push({ label: 'New thickness', value: num(input.newThick, 2) + ' mm' });
      rows.push({ label: 'Usable range', value: num(total, 2) + ' mm' });
      rows.push({ label: 'Used', value: num(Math.max(0, Math.min(100, used)), 0) + '%' });
      figures.push({ label: 'Service life used', value: num(Math.max(0, Math.min(100, used)), 0) + '%',
                     sub: 'Of the ' + num(total, 2) + 'mm between new and discard.' });
    }

    return { ok: true, errors: [], warnings: warnings, status: status, figures: figures, rows: rows };
  }

  /* ====================================================================
     Formatting
     ==================================================================== */
  function comma(n) {
    return String(n).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function num(n, dp) {
    if (!isFinite(n)) return '-';
    var v = Number(n).toFixed(dp == null ? 2 : dp);
    if (dp !== 0 && v.indexOf('.') > -1) v = v.replace(/\.?0+$/, '');
    var parts = v.split('.');
    return comma(parts[0]) + (parts[1] ? '.' + parts[1] : '');
  }

  function signed(n, dp) {
    if (!isFinite(n)) return '-';
    return (n > 0 ? '+' : '') + num(n, dp);
  }

  /* ====================================================================
     DOM wiring
     ==================================================================== */
  var FIELDS = [
      {
          "name": "measured",
          "key": "m",
          "type": "number"
      },
      {
          "name": "unit",
          "key": "u",
          "type": "select"
      },
      {
          "name": "discard",
          "key": "d",
          "type": "number"
      },
      {
          "name": "newThick",
          "key": "n",
          "type": "number"
      },
      {
          "name": "scored",
          "key": "s",
          "type": "checkbox"
      }
  ];

  var els = {
    error:    root.querySelector('[data-r-error]'),
    status:   root.querySelector('[data-r-status]'),
    statusLbl:root.querySelector('[data-r-status-label]'),
    statusVal:root.querySelector('[data-r-status-value]'),
    statusNote:root.querySelector('[data-r-status-note]'),
    figures:  root.querySelector('[data-r-figures]'),
    rows:     root.querySelector('[data-r-rows]'),
    warnings: root.querySelector('[data-r-warnings]')
  };

  var STATUS_CLASSES = ['is-good','is-monitor','is-soon','is-now','is-critical'];

  function field(name) { return root.querySelector('[data-f="' + name + '"]'); }

  function readInput() {
    var o = {};
    FIELDS.forEach(function (f) {
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') o[f.name] = el.checked;
      else if (f.type === 'number') o[f.name] = parseFloat(el.value);
      else o[f.name] = el.value;
    });
    return o;
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;');
  }

  function renderFigures(figs) {
    els.figures.innerHTML = (figs || []).map(function (f) {
      return '<div class="pc-rtc__figure">'
        + '<div class="pc-rtc__figure-label">' + esc(f.label) + '</div>'
        + '<div class="pc-rtc__figure-value' + (f.muted ? ' is-muted' : '') + '">'
        + esc(f.value) + '</div>'
        + (f.sub ? '<div class="pc-rtc__figure-sub">' + esc(f.sub) + '</div>' : '')
        + '</div>';
    }).join('');
  }

  function renderRows(rows) {
    if (!els.rows) return;
    els.rows.innerHTML = (rows || []).map(function (r) {
      if (r.head) {
        return '<div class="pc-rtc__row pc-rtc__rows-head">' + esc(r.head) + '</div>';
      }
      return '<div class="pc-rtc__row">'
        + '<span class="pc-rtc__row-label">' + esc(r.label) + '</span>'
        + '<span class="pc-rtc__row-value">' + esc(r.value) + '</span>'
        + '</div>';
    }).join('');
  }

  function clearResult(message) {
    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      els.statusVal.textContent  = 'Waiting for your numbers';
      els.statusNote.textContent = message || '';
    }
    els.figures.innerHTML = '';
    if (els.rows) els.rows.innerHTML = '';
    if (els.warnings) els.warnings.style.display = 'none';
  }

  function render() {
    var input  = readInput();
    var result = compute(input);

    if (!result.ok) {
      els.error.textContent = result.errors[0];
      els.error.classList.add('is-shown');
      clearResult(result.errors[0]);
      return;
    }
    els.error.classList.remove('is-shown');

    if (els.status) {
      STATUS_CLASSES.forEach(function (c) { els.status.classList.remove(c); });
      if (result.status) {
        els.status.classList.add('is-' + result.status.key);
        els.statusVal.textContent  = result.status.label;
        els.statusNote.textContent = result.status.note || '';
      } else {
        els.statusVal.textContent  = '';
        els.statusNote.textContent = '';
      }
    }

    renderFigures(result.figures);
    renderRows(result.rows);

    if (els.warnings) {
      if (result.warnings && result.warnings.length) {
        els.warnings.textContent = result.warnings[0];
        els.warnings.style.display = '';
      } else {
        els.warnings.style.display = 'none';
      }
    }

    writePermalink(input);
  }

  /* ====================================================================
     Shareable result permalinks
     ==================================================================== */
  function writePermalink(input) {
    if (!window.history || !window.history.replaceState) return;
    var q = FIELDS.map(function (f) {
      var v = input[f.name];
      if (f.type === 'checkbox') return v ? f.key + '=1' : '';
      if (v == null || v !== v || v === '') return '';
      return f.key + '=' + encodeURIComponent(v);
    }).filter(Boolean).join('&');
    try {
      window.history.replaceState(null, '', window.location.pathname + (q ? '?' + q : ''));
    } catch (e) {}
  }

  function applyPermalink() {
    if (!window.URLSearchParams) return;
    var p = new URLSearchParams(window.location.search);
    var any = false;
    FIELDS.forEach(function (f) {
      if (!p.has(f.key)) return;
      var el = field(f.name);
      if (!el) return;
      if (f.type === 'checkbox') el.checked = p.get(f.key) === '1';
      else el.value = p.get(f.key);
      any = true;
    });
    return any;
  }

  /* ====================================================================
     Init
     ==================================================================== */
  ['input', 'change'].forEach(function (evt) {
    root.addEventListener(evt, function (e) {
      if (e.target.closest('[data-r-form]')) render();
    });
  });

  applyPermalink();
  render();

  /* Exposed for testing and debugging only. */
  window.PC_RTC = { compute: compute };
})();
